import { Button, ScrollArea, Stack } from '@mantine/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { TSDData } from '@e-origin/contracts';
import { TSDBusinessValidationType, TSDStatus, TSDType } from '@e-origin/shared';

import { LeavePageNotification, StickyContainer } from '@components';
import { canEditTsdAfterPresentationNotificationFailure } from '@features/tsd/utils';
import { GoodsItemsList } from './GoodsItemsList';
import { HousesList } from './HousesList';
import { TSDViewTransferConsignment } from './TSDViewTransferConsignment';
import { useTSDViewConsignmentFormInitializer } from './useTSDViewConsignmentFormInitializer';

interface TSDViewConsignmentProps {
  tsd: TSDData;
  disabled: boolean;
}

enum TSDViewConsignmentContext {
  HOUSES_LIST = 'HOUSES_LIST',
  GOODS_LIST = 'GOODS_LIST',
}

export const TSDViewConsignment: React.FC<TSDViewConsignmentProps> = ({ tsd, disabled }) => {
  const { buildConsignmentValues, consignmentForm, onSubmit } = useTSDViewConsignmentFormInitializer({ tsd });

  const isTransferred = tsd.generalInfo?.type === TSDType.TRANSFERRED;

  const routeLocation = useLocation();
  const queryParams = useRef<URLSearchParams>(new URLSearchParams(routeLocation.search));
  const [context, setContext] = useState<TSDViewConsignmentContext>(TSDViewConsignmentContext.HOUSES_LIST);

  const showSendDeconsolidationButton = useMemo(() => {
    if (tsd.generalInfo.isAmended) {
      return false;
    }
    return !!(
      (tsd.generalInfo.status === TSDStatus.ACCEPTED && !tsd.generalInfo.isDeconsolidated) ||
      (tsd.generalInfo.status === TSDStatus.REJECTED && tsd.generalInfo.isDeconsolidatedRejected)
    );
  }, [tsd.generalInfo]);

  const transportDisabled = canEditTsdAfterPresentationNotificationFailure({
    status: tsd.generalInfo?.status,
    businessValidationType: tsd.customsState?.businessValidationType as TSDBusinessValidationType,
  })
    ? false
    : disabled;

  useEffect(() => {
    queryParams.current = new URLSearchParams(routeLocation.search);

    const queryContext = queryParams.current.get('houseRef');
    setContext(queryContext ? TSDViewConsignmentContext.GOODS_LIST : TSDViewConsignmentContext.HOUSES_LIST);
  }, [routeLocation]);

  useEffect(() => {
    consignmentForm.setValues(buildConsignmentValues(tsd));
    consignmentForm.resetDirty({});
  }, [tsd]);

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={consignmentForm.isDirty()} ignoreSearchParams={true} />

      <ScrollArea h="100%" offsetScrollbars="y">
        <form onSubmit={consignmentForm.onSubmit(onSubmit)}>
          {!isTransferred && (
            <Stack mx="xl">
              {[TSDViewConsignmentContext.HOUSES_LIST, null].includes(context) && (
                <HousesList
                  tsdName={tsd?.name}
                  consignmentForm={consignmentForm}
                  disabled={disabled}
                  transportDisabled={transportDisabled}
                  onSubmit={onSubmit}
                  showSendDeconsolidationButton={showSendDeconsolidationButton}
                />
              )}

              {context === TSDViewConsignmentContext.GOODS_LIST && <GoodsItemsList consignmentForm={consignmentForm} disabled={disabled} />}
            </Stack>
          )}

          {isTransferred && (
            <Stack mx="xl">
              <TSDViewTransferConsignment consignmentForm={consignmentForm} disabled={disabled} />
            </Stack>
          )}

          <StickyContainer>
            <Button type="submit" disabled={transportDisabled} variant="filled" size="lg" data-pw="save-transit-btn">
              Save
            </Button>
          </StickyContainer>
        </form>
      </ScrollArea>
    </>
  );
};
